import { AccordionItem } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  FilterStates,
  useNewsFilters,
} from './hooks/use-news-filters/useNewsFilters';
import * as S from './SideAccordionFilters.styles';

export enum ListType {
  Watchlists = 'watchlists',
  Markets = 'markets',
  Sources = 'sources',
}

interface AccordionItemFactoryProps {
  filterGroup: FilterStates;
  customClassName: S.FilterGroups;
  toggleAllLabel: string;
  listType: ListType;
}

interface SideAccordionFiltersProps {
  isShowProvider?: boolean;
}

export const SideAccordionFilters = ({
  isShowProvider = true,
}: SideAccordionFiltersProps) => {
  const { t } = useTranslation(['news']);

  const PROVIDER_TITLE = t('news:sideAccordionFilters.provider');

  const {
    watchlistStates,
    marketsStates,
    sourcesStates,
    handleToggleAll,
    handleToggleOne,
  } = useNewsFilters();

  const accordionItemFactory = ({
    filterGroup,
    customClassName,
    toggleAllLabel,
    listType,
  }: AccordionItemFactoryProps) => {
    const { listToCheck, checkedCount, isPartialChecked, isAllChecked } =
      filterGroup;

    const genRightAlignedChild = () => {
      if (!checkedCount) {
        return null;
      }

      if (checkedCount === 1) {
        return (
          <S.SingleSelectedItem>
            {listToCheck.filter(item => item.isChecked)[0]?.name}
          </S.SingleSelectedItem>
        );
      }

      return (
        <S.StyledBadge
          variant="numerical"
          label={checkedCount}
          size="small"
          data-testid={`count-badge-${customClassName}`}
        />
      );
    };

    return {
      className: checkedCount ? 'option-selected' : undefined,
      content: (
        <S.AccordionContentWrapper>
          <S.StyledCheckbox
            label={toggleAllLabel}
            name={toggleAllLabel}
            checked={isPartialChecked || isAllChecked}
            partial={isPartialChecked}
            onChange={() => handleToggleAll(listType, isAllChecked)}
            data-testid={`toggle-all-${listType}`}
          />
          {listToCheck.map(item => {
            return (
              <S.StyledCheckbox
                key={item.key}
                label={item.name}
                name={item.name}
                checked={item.isChecked}
                onChange={() => handleToggleOne(listType, item.key)}
              />
            );
          })}
        </S.AccordionContentWrapper>
      ),
      rightAlignedChild: genRightAlignedChild(),
    };
  };

  const defaultAccordionItems: AccordionItem[] = [
    {
      title: t('news:sideAccordionFilters.watchlist'),
      ...accordionItemFactory({
        filterGroup: watchlistStates,
        customClassName: S.FilterGroups.Watchlist,
        toggleAllLabel: t('news:tabs.allWatchlists'),
        listType: ListType.Watchlists,
      }),
    },
    {
      title: PROVIDER_TITLE,
      ...accordionItemFactory({
        filterGroup: sourcesStates,
        customClassName: S.FilterGroups.Provider,
        toggleAllLabel: t('news:newsFilters.allProviders'),
        listType: ListType.Sources,
      }),
    },
    {
      title: t('news:sideAccordionFilters.countries'),
      ...accordionItemFactory({
        filterGroup: marketsStates,
        customClassName: S.FilterGroups.Countries,
        toggleAllLabel: t('news:newsFilters.allIndustries'),
        listType: ListType.Markets,
      }),
    },
  ];

  return (
    <S.SideAccordionFiltersRoot>
      <S.StyledAccordion
        data-testid="accordion-filters"
        items={
          isShowProvider
            ? defaultAccordionItems
            : defaultAccordionItems.filter(
                item => item.title !== PROVIDER_TITLE
              )
        }
      />
    </S.SideAccordionFiltersRoot>
  );
};
